import { useState, useEffect, useMemo } from "react";
import { FiShoppingCart } from "react-icons/fi";
import { IoHeartOutline } from "react-icons/io5";
import { useNavigate, useLocation } from "react-router-dom";
import { addToCartThunk } from "../../redux/actions/cartActions";
import { toggleWishlistItem } from "../../redux/actions/wishlistAction";
import * as other from "../other";
import { useDispatch, useSelector } from "react-redux";
import { getFilteredProducts } from "../../redux/actions/productFilterActions";
import { toast } from "react-toastify";
import * as Alert from "../common/Alert";
import { checkStockAvailability } from "../../redux/actions/stockActions";

const Products = ({ products = [], pagination = {} }) => {
  const location = useLocation();
  const { category, subCategory } = location.state || {};

  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subProducts, setSubProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [currentProductId, setCurrentProductId] = useState(null);

  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  // Get all product IDs in wishlist
  const wishlistProductIds = wishlistItems.map(
    (item) => item.product?.pro_id || item.pro_id
  );

  const totalPages = Math.ceil(pagination.total_products / rowsPerPage);

  const handleAddToCart = (e, product) => {
    e.stopPropagation();

    if (product.SubProducts?.length) {
      setSubProducts(product.SubProducts);
      setIsModalOpen(true);
      // Save current product ID so we can use it in handleAddSubProductToCart
      setCurrentProductId(product.pro_id);
    } else {
      dispatch(
        addToCartThunk({
          product_id: product.pro_id,
          sub_product_id: null,
          quantity: 1,
        })
      );
    }
  };

  const handleAddSubProductToCart = (sub) => {
    const sub_product_id = sub.sub_pro_id;
    const quantity = quantities[sub_product_id] || 1;

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          Alert.Alert.ErrorAlert("Insufficient stock!");
        } else {
          // Stock is available, increment quantity
          dispatch(
            addToCartThunk({
              product_id: currentProductId,
              sub_product_id,
              quantity,
            })
          );
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const incrementQuantity = (sub) => {
    let temp = { ...quantities };
    const currentQty = parseInt(temp[sub.sub_pro_id] ?? 1);
    const pcsPerItem = parseInt(sub.no_of_pcs);
    const stock = parseInt(sub.no_of_stock);

    const qty =
      userType === "vendor" ? (currentQty + 1) * pcsPerItem : currentQty + 1;

    if (qty > stock) {
      toast.info("Insufficient Stock !!!");
    } else {
      setQuantities((prev) => ({
        ...prev,
        [sub.sub_pro_id]: (temp[sub.sub_pro_id] || 1) + 1,
      }));
    }
  };

  const decrementQuantity = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    dispatch(toggleWishlistItem(product.pro_id));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [products]);

  let filteredProducts = products;
  if (category) {
    filteredProducts = filteredProducts.filter(
      (p) =>
        (p.category || p.cat_name || "").toLowerCase() ===
        category.toLowerCase()
    );
  }
  if (subCategory) {
    filteredProducts = filteredProducts.filter(
      (p) => (p.sub_cat_name || "").toLowerCase() === subCategory.toLowerCase()
    );
  }

  return (
    <>
      {products.length > 0 ? (
        products.map((product) => (
          <div
            key={product.pro_id}
            onClick={() => navigate(`/ProductsDetails/${product.pro_id}`)}
            className="cursor-pointer relative"
          >
            {product?.product_primary_img && (
              <div className="aspect-[3/4] overflow-hidden rounded-3xl">
                <img
                  loading="lazy"
                  src={product?.product_primary_img}
                  alt={product.product_name}
                  className="size-full"
                />
              </div>
            )}
            <div className="flex flex-col md:flex-row justify-between relative z-10 inset-0 group-hover:text-black group-hover:bg-purple-100 bg-opacity-0 group-hover:bg-opacity-80 rounded-lg lg:m-1.5 p-1.5 ">
              <div className="flex flex-col min-w-0 max-w-[70%]">
                <p className="font-medium text-base md:text-lg break-words leading-tight">
                  {product.product_name}
                </p>
                <p className="text-sm text-gray-600 mt-2 break-words leading-tight">
                  {product.product_sub_title}
                </p>
                <p className="text-sm text-gray-600 mt-2 break-words leading-tight">
                  {product.sub_cat_name}
                </p>
              </div>
              {/* Price aligned right and centered */}
              {isAuthenticated &&
                product.SubProducts &&
                product.SubProducts.length > 0 && (
                  <div className="flex items-center justify-end ml-2 min-w-[4.5rem] max-w-[7rem]">
                    <p className="text-base font-semibold text-gray-700 whitespace-nowrap">
                      {userType === "vendor"
                        ? product.SubProducts[0].vendor_product_price
                        : product.SubProducts[0].customer_product_price}
                      <span className="text-base"> ₹</span>
                    </p>
                  </div>
                )}
            </div>
            {isAuthenticated && (
              <div className="absolute top-2 right-2 flex flex-col gap-2">
                <span
                  className={`p-2 rounded-full cursor-pointer transition-colors ${
                    wishlistProductIds.includes(product.pro_id)
                      ? "bg-red-500 text-white"
                      : "bg-tealish text-gray-700"
                  }`}
                  onClick={(e) => handleToggleWishlist(e, product)}
                >
                  <IoHeartOutline className="text-white" />
                </span>
                <span
                  className="bg-tealish p-2 rounded-full"
                  onClick={(e) => handleAddToCart(e, product)}
                >
                  <FiShoppingCart className="text-white" />
                </span>
              </div>
            )}
          </div>
        ))
      ) : (
        <p>No Products</p>
      )}
      <other.Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Select Sub Product"
      >
        <div className="flex flex-col gap-4">
          {subProducts.map((sub, idx) => (
            <div
              key={idx}
              className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
            >
              {/* Image on the left */}
              <img
                loading="lazy"
                src={sub.sub_pro_img}
                alt={`sub-product-${idx}`}
                className="w-32 h-44 object-cover hover:scale-110 transition-transform ease-in-out duration-500 cursor-pointer rounded-lg flex-shrink-0"
              />

              {/* Details on the right */}
              <div className="flex flex-col justify-between flex-grow h-full">
                <div className="space-y-1">
                  <p>
                    <strong>Color:</strong> {sub.color_id}
                  </p>
                  <p>
                    <strong>Size:</strong> {sub.size}
                  </p>
                  {/* <p><strong>Location:</strong> {sub.location}</p> */}
                  {isAuthenticated ? (
                    <p>
                      <strong>Price:</strong> ₹
                      {userType === "vendor"
                        ? sub.set_price
                        : sub.customer_product_price}
                    </p>
                  ) : (
                    ""
                  )}
                </div>

                {/* Quantity controls */}
                <div className="self-start flex items-center mt-2 gap-2 ">
                  <div className="border border-gray-300 rounded-full overflow-hidden">
                    <button
                      onClick={() => decrementQuantity(sub.sub_pro_id)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      disabled={quantities[sub.sub_pro_id] <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 py-1 select-none">
                      {quantities[sub.sub_pro_id] || 1}
                    </span>
                    <button
                      onClick={() => incrementQuantity(sub)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    >
                      +
                    </button>
                  </div>

                  {userType === "vendor" ? (
                    sub.selling_type == "set" ? (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Set" : "Sets"}{" "}
                      </span>
                    ) : (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Piece" : "Pieces"}
                      </span>
                    )
                  ) : null}
                </div>

                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  {/* Note Text */}
                  {userType === "vendor" &&
                  sub.selling_type == "set" &&
                  sub.no_of_pcs ? (
                    <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
                      {` ${quantities[sub.sub_pro_id] || 1} Set = ${
                        sub.no_of_pcs * (quantities[sub.sub_pro_id] || 1)
                      } pieces`}{" "}
                      (₹{sub.set_price * (quantities[sub?.sub_pro_id] || 1)})
                    </p>
                  ) : (
                    // Invisible div to take up the same space so button stays right-aligned
                    <div className="min-w-[180px]" />
                  )}

                  {/* Add Button */}
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-1 w-full sm:w-fit">
                      <button
                        className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleAddSubProductToCart(sub)}
                        disabled={sub.no_of_stock <= 0}
                      >
                        Add Item
                      </button>

                      {sub.no_of_stock <= 0 && (
                        <p className="text-red-600 text-sm font-semibold">
                          Out of Stock
                        </p>
                      )}
                    </div>
                  ) : (
                    navigate("/login")
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </other.Modal>
    </>
    // <div className="w-full overflow-hidden overflow-y-scroll flex-1 flex flex-col">
    //   {products.length > 0 ? (
    //     <div className="flex flex-col h-full">
    //       <div className="flex-1 lg:overflow-y-auto">
    //         <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">

    //         </div>
    //       </div>
    //     </div>
    //   ) : (
    //     <div className="flex w-full justify-center items-center">
    //       <span className="text-gray-500">No Data Avalailable</span>
    //     </div>
    //   )}

    //   <other.Modal
    //     isOpen={isModalOpen}
    //     onClose={() => setIsModalOpen(false)}
    //     title="Select Sub Product"
    //   >
    //     <div className="flex flex-col gap-4">
    //       {subProducts.map((sub, idx) => (
    //         <div
    //           key={idx}
    //           className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
    //         >
    //           {/* Image on the left */}
    //           <img loading='lazy'
    //             src={sub.sub_pro_img}
    //             alt={`sub-product-${idx}`}
    //             className="w-32 h-44 object-cover hover:scale-110 transition-transform ease-in-out duration-500 cursor-pointer rounded-lg flex-shrink-0"
    //           />

    //           {/* Details on the right */}
    //           <div className="flex flex-col justify-between flex-grow h-full">
    //             <div className="space-y-1">
    //               <p>
    //                 <strong>Color:</strong> {sub.color_id}
    //               </p>
    //               <p>
    //                 <strong>Size:</strong> {sub.size}
    //               </p>
    //               {/* <p><strong>Location:</strong> {sub.location}</p> */}
    //               {isAuthenticated ? (
    //                 <p>
    //                   <strong>Price:</strong> ₹
    //                   {userType === "vendor"
    //                     ? sub.set_price
    //                     : sub.customer_product_price}
    //                 </p>
    //               ) : (
    //                 ""
    //               )}
    //             </div>

    //             {/* Quantity controls */}
    //             <div className="self-start flex items-center mt-2 gap-2 ">
    //               <div className="border border-gray-300 rounded-full overflow-hidden">
    //                 <button
    //                   onClick={() => decrementQuantity(sub.sub_pro_id)}
    //                   className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
    //                   disabled={quantities[sub.sub_pro_id] <= 1}
    //                 >
    //                   -
    //                 </button>
    //                 <span className="px-4 py-1 select-none">
    //                   {quantities[sub.sub_pro_id] || 1}
    //                 </span>
    //                 <button
    //                   onClick={() => incrementQuantity(sub)}
    //                   className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
    //                 >
    //                   +
    //                 </button>
    //               </div>

    //               {userType === "vendor" ? (
    //                 sub.selling_type == "set" ? (
    //                   <span className="ml-2">
    //                     {quantities[sub.sub_pro_id] <= 1 ? "Set" : "Sets"}{" "}
    //                   </span>
    //                 ) : (
    //                   <span className="ml-2">
    //                     {quantities[sub.sub_pro_id] <= 1 ? "Piece" : "Pieces"}
    //                   </span>
    //                 )
    //               ) : null}
    //             </div>

    //             <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
    //               {/* Note Text */}
    //               {userType === "vendor" &&
    //               sub.selling_type == "set" &&
    //               sub.no_of_pcs ? (
    //                 <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
    //                   {` ${quantities[sub.sub_pro_id] || 1} Set = ${
    //                     sub.no_of_pcs * (quantities[sub.sub_pro_id] || 1)
    //                   } pieces`}{" "}
    //                   (₹{sub.set_price * (quantities[sub?.sub_pro_id] || 1)})
    //                 </p>
    //               ) : (
    //                 // Invisible div to take up the same space so button stays right-aligned
    //                 <div className="min-w-[180px]" />
    //               )}

    //               {/* Add Button */}
    //               {isAuthenticated ? (
    //                 <div className="flex flex-col gap-1 w-full sm:w-fit">
    //                   <button
    //                     className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
    //                     onClick={() => handleAddSubProductToCart(sub)}
    //                     disabled={sub.no_of_stock <= 0}
    //                   >
    //                     Add Item
    //                   </button>

    //                   {sub.no_of_stock <= 0 && (
    //                     <p className="text-red-600 text-sm font-semibold">
    //                       Out of Stock
    //                     </p>
    //                   )}
    //                 </div>
    //               ) : (
    //                 navigate("/login")
    //               )}
    //             </div>
    //           </div>
    //         </div>
    //       ))}
    //     </div>
    //   </other.Modal>
    // </div>
  );
};

export default Products;
